import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const UPDATED = "Last updated: 21 September 2026";

const SECTIONS = [
  {
    h: "1. About these terms",
    p: [
      "These Terms & Conditions (\u201cTerms\u201d) govern your registration for and participation in The Ageless Woman retreat (the \u201cRetreat\u201d), a women's vitality experience held in Florence and Tuscany, Italy, from 25 April to 2 May 2027, led by Natalie Alcantara and Monica Pandele and operated by Gefyra (\u201cwe\u201d, \u201cus\u201d, \u201cour\u201d, the \u201cOrganiser\u201d).",
      "By making the reservation downpayment, you (\u201cyou\u201d, \u201cthe Guest\u201d) confirm that you have read, understood and agree to be bound by these Terms.",
    ],
  },
  {
    h: "2. Reservation & downpayment",
    p: [
      "A non-refundable reservation downpayment of USD $750 secures your place. Places are limited to an intimate circle of 8\u201310 women and are allocated upon acceptance of your application and receipt of the downpayment.",
      "The downpayment is applied in full toward your total Retreat fee.",
    ],
  },
  {
    h: "3. Pricing",
    p: [
      "The full Retreat investment is USD $6,497 in a shared room, or USD $6,997 for a private single room.",
      "All prices are stated in US Dollars. The balance and its payment schedule are confirmed in writing upon registration.",
    ],
  },
  {
    h: "4. Payment plan",
    p: [
      "A payment plan is available. Payments are arranged in two to three installments, agreed together on a case-by-case basis to suit your circumstances, with the full balance completed ahead of the Retreat on a schedule agreed once dates are confirmed.",
      "Missed payments may result in the loss of your place; please contact us promptly if your circumstances change so we can find a solution together.",
    ],
  },
  {
    h: "5. What is included & excluded",
    p: [
      "Included: shared accommodation in Tuscany, the full guided programme, nourishment at the Tuscan table (continental breakfasts, light \u201con-the-go\u201d lunch options and nourishing dinners), the HeartMath assessment (before and after), and pre-retreat preparation and post-retreat integration.",
      "Not included: flights to and from Italy, travel and medical insurance, personal expenses, and anything not expressly listed as included.",
    ],
  },
  {
    h: "6. Cancellations, transfers & refunds",
    p: [
      "The $750 downpayment is non-refundable but is fully transferable to a future retreat within twelve (12) months, subject to availability.",
      "In exceptional, unforeseen circumstances \u2014 such as a medical emergency, a death in the immediate family, or a genuine inability to travel \u2014 we will work with you compassionately on a case-by-case basis to find a fair resolution, which may include a credit or refund of amounts paid.",
      "Cancellation terms for amounts paid beyond the downpayment are provided in full at registration. We strongly recommend comprehensive travel insurance that covers cancellation, curtailment and medical costs (see clause 8).",
    ],
  },
  {
    h: "7. Minimum participation & postponement",
    p: [
      "The Retreat requires a minimum of six (6) confirmed participants to proceed as scheduled. If this minimum is not met, or where circumstances reasonably outside our control make it impractical to deliver the Retreat as intended, the Organiser reserves the right to postpone or reschedule the Retreat.",
      "Where we postpone, we will notify registered Guests in writing no less than four (4) weeks (28 days) before the scheduled start date. Affected Guests may then, at their election: (a) transfer their full payment to the rescheduled dates; (b) transfer their full payment as a credit toward a future Retreat within twelve (12) months; or (c) receive a full refund of all amounts paid to the Organiser.",
      "Our liability in the event of postponement is limited to the total amounts paid by the Guest to the Organiser for the Retreat. We are not responsible for any third-party costs a Guest may incur (including flights, travel insurance, visas or other bookings). Force majeure: the Organiser shall not be liable for any failure or delay caused by events beyond its reasonable control (including natural disaster, epidemic or pandemic, government action, war or civil unrest); in such cases the options above shall apply.",
    ],
  },
  {
    h: "8. Mandatory travel & medical insurance",
    p: [
      "All international participants are required to purchase comprehensive travel medical insurance prior to the Retreat, with a minimum of USD $100,000 in emergency medical and emergency evacuation / repatriation coverage. Proof of a valid policy may be requested before the Retreat.",
      "The Organiser is not responsible for any medical, evacuation, repatriation, trip-cancellation, trip-interruption or personal-property costs incurred by the Guest. Guests travel and participate at their own risk, and appropriate insurance is the Guest's sole responsibility.",
    ],
  },
  {
    h: "9. Health, wellbeing & assumption of risk",
    p: [
      "The Retreat includes movement, travel and experiential practices. You are responsible for ensuring you are in suitable health to participate and for disclosing any relevant medical conditions in advance. The Retreat does not constitute medical, psychological or therapeutic advice or treatment.",
      "You participate voluntarily and assume the ordinary risks associated with travel and group activities. To the fullest extent permitted by law, we are not liable for personal injury, illness, loss or damage save where caused by our negligence.",
    ],
  },
  {
    h: "10. Conduct",
    p: [
      "The Retreat is a shared, respectful space. We may, at our discretion and without refund, decline or discontinue participation where a Guest's conduct materially affects the safety, wellbeing or experience of others.",
    ],
  },
  {
    h: "11. Changes to the retreat",
    p: [
      "We reserve the right to make reasonable adjustments to the itinerary, accommodation, guides or activities where necessary, while preserving the overall character and value of the experience.",
    ],
  },
  {
    h: "12. Privacy",
    p: [
      "Personal information you provide is used only to administer your booking and to communicate with you about the Retreat. We do not sell your data. Photography or video captured during the Retreat may be used for promotional purposes only with your prior consent.",
    ],
  },
  {
    h: "13. Governing law & contact",
    p: [
      "These Terms constitute the agreement between you and the Organiser regarding the Retreat and are governed by the laws of the jurisdiction in which the Organiser (Gefyra) is established. Questions? Email hello@agelesswomanretreat.com and we'll be glad to help.",
    ],
  },
];

export default function Terms() {
  return (
    <main className="min-h-screen bg-[#F7F5F0] text-[#2C2A28]" data-testid="terms-page">
      <header className="border-b hairline">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl tracking-tight hover:text-[#B85C38] transition-colors" data-testid="terms-home-link">
            The Ageless Woman
          </Link>
          <Link to="/" data-testid="terms-back-link"
            className="inline-flex items-center gap-2 font-mono-a text-[11px] uppercase tracking-[0.16em] text-[#595550] hover:text-[#B85C38] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>

      <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <p className="font-mono-a text-[11px] uppercase tracking-[0.25em] text-[#B85C38] mb-6">Terms &amp; Conditions</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
          The fine print, <span className="italic">held with care.</span>
        </h1>
        <p className="mt-5 text-[#595550] leading-relaxed max-w-2xl">
          Florence &amp; Tuscany, Italy · 25 April – 2 May 2027. Please read these terms before securing your place. {UPDATED}.
        </p>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s) => (
            <div key={s.h}>
              <h2 className="font-serif text-2xl lg:text-3xl tracking-tight leading-tight">{s.h}</h2>
              <div className="mt-3 space-y-3">
                {s.p.map((para, i) => (
                  <p key={i} className="text-[#595550] text-lg leading-relaxed">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t hairline">
          <Link to="/#reserve" data-testid="terms-reserve-link"
            className="inline-flex items-center gap-2 font-mono-a text-[11px] uppercase tracking-[0.16em] text-[#2C2A28] border-b border-[#B85C38] pb-1 hover:text-[#B85C38] transition-colors">
            Return to reserve your place
          </Link>
        </div>
      </section>
    </main>
  );
}
